// src/pages/Home.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
    Clock, Calendar, CheckCircle, XCircle, Download, BookOpen,
    Trophy, Bell, UserPlus, FileText, FileCheck, ExternalLink,
    Key, Zap, DollarSign, Users, Ticket, Activity // Added Activity icon for Live Events section
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';

// --- Utility Components (Unchanged) ---

function DigitalClock() {
    const [currentTime, setCurrentTime] = useState(new Date());
    useEffect(() => {
        const timerId = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timerId);
    }, []);
    const getOrdinal = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };
    const formatFullDate = (date) => {
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'long' });
        const year = date.getFullYear();
        const ordinal = getOrdinal(day);
        return `${day}${ordinal} Day of ${month}, ${year}`;
    };
    const formatTime = (date) => {
        const h = String(date.getHours()).padStart(2, '0');
        const m = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };
    return (
        <div className="flex flex-col items-end space-y-1 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
            <div className="text-2xl font-mono font-bold text-indigo-700 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                {formatTime(currentTime)}
            </div>
            <div className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">
                {formatFullDate(currentTime)}
            </div>
        </div>
    );
}

function PublicExamCard({ exam }) {
    const isRegistrationActive = exam.registrationStatus === 'Ongoing';
    const isResultsPublished = exam.isResultDeclared;
    const scheduledDate = exam.scheduledTime ? new Date(exam.scheduledTime).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Date TBD';
    let statusText = isResultsPublished ? 'RESULTS DECLARED' : isRegistrationActive ? 'REGISTRATION OPEN' : 'UPCOMING';
    let statusClass = isResultsPublished ? 'bg-purple-100 text-purple-700' : isRegistrationActive ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700';
    let actionButtonClass = isRegistrationActive ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed';
    return (
        <div className="flex flex-col h-full border border-gray-300 rounded-xl shadow-lg transition duration-300 hover:shadow-2xl bg-white">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${statusClass} flex items-center`}>
                    {isRegistrationActive ? <CheckCircle className="w-4 h-4 mr-1" /> : isResultsPublished ? <Trophy className="w-4 h-4 mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
                    {statusText.toUpperCase()}
                </span>
                <span className="text-sm font-medium text-gray-600">{exam.format ? exam.format.toUpperCase() : 'N/A'}</span>
            </div>
            <div className="p-5 flex-grow space-y-3">
                <h3 className="text-xl font-bold text-gray-800">{exam.title || 'Untitled Exam'}</h3>
                <div className="text-sm text-gray-700 flex items-center"><BookOpen className="w-4 h-4 mr-2 text-indigo-500" />Subject: <span className="ml-1 font-semibold">{exam.subject || 'N/A'}</span></div>
                <div className="text-sm text-gray-700 flex items-center"><Calendar className="w-4 h-4 mr-2 text-indigo-500" />Apply By: <span className="ml-1 font-semibold">{scheduledDate}</span></div>
                <a href={exam.advertisementUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center pt-2"><Download className="w-3 h-3 mr-1" /> View Full Details (Advert)</a>
            </div>
            <div className="p-4 border-t">
                {isRegistrationActive && !isResultsPublished ? (
                    // This Link now correctly points to the root, which will trigger the login/signup
                    <Link to="/" state={{ paperId: exam.paperId }} className={`w-full inline-block text-center p-3 text-white font-bold rounded-lg ${actionButtonClass}`}>APPLY NOW</Link>
                ) : isResultsPublished ? (
                    <Link to="/results/public" className="w-full inline-block text-center p-3 font-bold rounded-lg bg-purple-600 text-white hover:bg-purple-700">VIEW RESULTS</Link>
                ) : (
                    <button disabled className={`w-full p-3 text-white font-bold rounded-lg ${actionButtonClass}`}>REGISTRATION CLOSED</button>
                )}
            </div>
        </div>
    );
}

function QuickLinkButton({ title, icon, color, action }) {
    const IconComponent = { UserPlus, Download, BookOpen, Trophy, FileText, FileCheck, Key, Zap, DollarSign, Ticket, Users, Activity }[icon]; // Ensure Ticket and Activity icons are included
    const colorClasses = {
        green: 'bg-green-600 hover:bg-green-700',
        blue: 'bg-blue-600 hover:bg-blue-700',
        orange: 'bg-orange-600 hover:bg-orange-700',
        purple: 'bg-purple-600 hover:bg-purple-700',
        red: 'bg-red-600 hover:bg-red-700',
        indigo: 'bg-indigo-600 hover:bg-indigo-700', // Added indigo for the new button
        teal: 'bg-teal-600 hover:bg-teal-700',
    };
    const colorClass = colorClasses[color] || 'bg-gray-600 hover:bg-gray-700';
    return (<button onClick={action} className={`flex flex-col items-center justify-center p-3 text-white rounded-lg font-semibold h-20 transition duration-150 shadow-lg ${colorClass}`}>{IconComponent && <IconComponent className="w-6 h-6 mb-1" />}<span className="text-xs text-center">{title}</span></button>);
}

// --- 4. Main Home Page Component ---
export default function Home() {
    const { userProfile, signInWithGoogle, loading: authContextLoading, fetchPublicPortalData } = useAuth();

    const navigate = useNavigate();

    const [publicData, setPublicData] = useState({ exams: [], recentResults: [], updates: [], courses: [], events: [] });
    const [dataLoading, setDataLoading] = useState(true);
    const [authLoading, setAuthLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeModal, setActiveModal] = useState(null);
    const [modalContentLoading, setModalContentLoading] = useState(false);
    const [modalContentData, setModalContentData] = useState(null);

    const exams = publicData?.exams || [];
    const recentResults = publicData?.recentResults || [];
    const updates = publicData?.updates || [];
    const courses = publicData?.courses || [];
    const events = publicData?.events || []; // New events data

    // Define the specific path for participants
    const studentDashboardPath = '/student/dashboard';

    // --- EFFECT FOR REDIRECTION ---
    useEffect(() => {
        if (authContextLoading) return; // Wait
        if (userProfile) {
            console.log("Redirect check. Profile status:", userProfile.status, "Role:", userProfile.role);

            // Define the generic dashboard path based on role (for staff/admin)
            const targetPath = `/${userProfile.role || 'student'}/dashboard`;

            if (userProfile.status === 'approved') {
                // FIX: If the user is student/guest, send them to student dashboard. Otherwise, their specific role dashboard.
                const finalRedirectPath = userProfile.role === 'student' || userProfile.role === 'guest'
                                            ? studentDashboardPath
                                            : targetPath;

                console.log(`Redirecting to ${finalRedirectPath}...`);
                navigate(finalRedirectPath, { replace: true });
            } else if (userProfile.status === 'pending_approval') {
                console.log("Redirecting to /pending-approval...");
                navigate('/pending-approval', { replace: true });
            }
        }
    }, [userProfile, authContextLoading, navigate]);
    // --- END REDIRECT EFFECT ---

    // Effect to load public data
    useEffect(() => {
        const loadPublicData = async () => {
            setDataLoading(true);
            setError(null);
            try {
                // Fetch public portal data, which should now include events
                const data = await fetchPublicPortalData();
                setPublicData({
                    exams: data?.exams ?? [],
                    recentResults: data?.recentResults ?? [],
                    updates: data?.updates ?? [],
                    courses: data?.courses ?? [],
                    events: data?.events ?? [], // Assume public data fetching includes events
                });
            } catch (err) {
                // This block catches the CORS error
                console.error("Public Data Fetch Error:", err);
                setError(`Failed to load portal data. Check Network Configuration: ${err.message}.`);
            } finally {
                setDataLoading(false);
            }
        };

        if (!authContextLoading && !userProfile) {
            loadPublicData();
        } else if (!authContextLoading && userProfile && userProfile.status !== 'approved' && userProfile.status !== 'pending_approval') {
             loadPublicData();
        } else if (authContextLoading) {
             setDataLoading(true);
        } else {
             setDataLoading(false);
        }
    }, [fetchPublicPortalData, authContextLoading, userProfile]);

    // Handle Google Sign-In Click
    const handleGoogleSignIn = async () => {
        setError(null);
        setAuthLoading(true);
        try {
            await signInWithGoogle();
            // Redirect logic is now handled by the useEffect hook
        } catch (error) {
            console.error("Google Sign-In Error on Home:", error);
            if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
                setError(`Login failed: ${error.message}`);
            }
        } finally {
            setAuthLoading(false);
        }
    };

    // Modal data fetch logic
    const handleModalDataFetch = (type) => {
        setActiveModal(type);
        setModalContentLoading(true);
        setModalContentData(null);
        setError(null);
        try {
            if (type === 'courses') setModalContentData(courses);
            else if (type === 'apply') setModalContentData(exams.filter(e => e.registrationStatus === 'Ongoing'));
            else if (type === 'result') setModalContentData(recentResults);
            else if (type === 'advert') setModalContentData(updates);
            else if (type === 'events') setModalContentData(events); // NEW: Events/Tickets
        } catch (err) {
            setError(`Error fetching modal data: ${err.message}`);
        } finally {
            setModalContentLoading(false);
        }
    };

    // Modal render logic (Full version)
    const renderModalContent = () => {
        const dataList = modalContentData || [];
        const getNoDataMessage = () => {
             switch(activeModal) {
                 case 'courses': return 'No courses or classes available at the moment.';
                 case 'apply': return 'No exams are currently open for application.';
                 case 'result': return 'No declared results available.';
                 case 'advert': return 'No advertisements or announcements available.';
                 case 'events': return 'No upcoming events or live classes are scheduled.'; // NEW
                 default: return 'No items available.';
             }
        };

        if (modalContentLoading) { return <div className="text-center p-5 text-indigo-600 font-semibold">Loading data...</div>; }
        if (error && activeModal) { return <div className="p-4 bg-red-100 text-red-700 border border-red-300 rounded-md">{error}</div>; }

        // Logic to dynamically determine the participant type for the restricted message
        const isStudentFeature = (activeModal === 'admit' || activeModal === 'answer' || activeModal === 'result');
        const participantType = isStudentFeature ? 'student' : 'participant';

        // FIX: The restricted prompt now applies to all participant-specific features
        if (activeModal === 'admit' || activeModal === 'answer' || activeModal === 'courses' || activeModal === 'result' || activeModal === 'events') {
            return (
                <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-md">
                    <p className="font-semibold text-lg mb-2 text-yellow-700">Access Restricted</p>
                    <p className="text-gray-700">Please **log in to your {participantType} dashboard** for secure access to tickets, results, and courses.</p>
                    {/* The login button uses the general sign-in and will be redirected by useEffect to the student dashboard */}
                    <button onClick={() => { handleGoogleSignIn(); setActiveModal(null); }} className="mt-3 inline-block text-indigo-600 hover:underline font-semibold">Login to Continue</button>
                </div>
            );
        }

        if (activeModal === 'advert') {
             const advertList = dataList;
             if (advertList.length === 0) { return <p className="p-4 text-gray-500">{getNoDataMessage()}</p>; }
             return ( <div className="space-y-3"><h3 className="text-xl font-bold border-b pb-2">All Announcements</h3>{advertList.map((update, index) => ( <div key={index} className="p-3 border rounded-md bg-gray-50 flex justify-between items-center"><div><p className="font-medium">{update.text}</p><p className="text-xs text-gray-500">Date: {update.date ? new Date(update.date).toLocaleDateString() : 'N/A'} - Status: {update.status || 'New'}</p></div><a href={update.advertisementUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 flex items-center text-sm ml-4 flex-shrink-0"><Download className="w-4 h-4 mr-1" /> PDF</a></div> ))}</div> );
        }

        if (dataList.length === 0 && !modalContentLoading) { return <p className="p-4 text-gray-500">{getNoDataMessage()}</p>; }

        switch (activeModal) {
            case 'courses':
            case 'result':
            case 'admit':
            case 'answer':
            case 'events':
                // The actual content rendering is now handled by the 'Access Restricted' block above,
                // but we keep the case statements for structure.
                return <div className="p-4 text-center text-gray-500">Redirecting to login...</div>;
            case 'apply':
                return (
                    <div className="space-y-4"><h3 className="text-xl font-bold border-b pb-2">Exams Open for Application</h3>{dataList.map(exam => (
                        <div key={exam.id || exam.paperId} className="p-3 border rounded-md flex justify-between items-center bg-green-50 border-green-200">
                            <span className="font-semibold text-green-800">{exam.title}</span>
                            <button onClick={() => { handleGoogleSignIn(); setActiveModal(null); }} className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 font-medium">Apply Now</button>
                        </div>
                    ))}</div>
                );
            default: return null;
        }
    };

    if (authContextLoading) {
         return <div className="min-h-screen flex items-center justify-center text-lg font-semibold">Loading Portal...</div>
    }

    // --- Main Component JSX ---
    return (
        <div className="bg-gray-50 min-h-screen flex flex-col">
            {activeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center border-b pb-3 mb-4 flex-shrink-0">
                            <h2 className="text-2xl font-bold text-indigo-700">
                                {activeModal === 'apply' ? 'Apply Online' :
                                 activeModal === 'admit' ? 'Admit Card Access' :
                                 activeModal === 'answer' ? 'Answer Key Release' :
                                 activeModal === 'result' ? 'Declared Results' :
                                 activeModal === 'courses' ? 'Course Catalog' :
                                 activeModal === 'events' ? 'Events & Tickets' : // NEW
                                 'Announcements'}
                            </h2>
                            {/* ✅ --- FIX: Corrected closing tag --- */}
                            <button onClick={() => setActiveModal(null)} className="text-gray-500 hover:text-gray-900 font-bold text-3xl leading-none">&times;</button>
                        </div>
                        <div className="overflow-y-auto pr-2 flex-grow">
                             {renderModalContent()}
                        </div>
                    </div>
                </div>
            )}

            <header className="bg-white shadow-md p-4 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <h1 className="text-3xl font-black text-indigo-700">Math Craft Portal</h1>
                    <div className="flex items-center space-x-6">
                        <DigitalClock />
                        {userProfile ? (
                            <Link
                                // FIX: Use the specific student path if they are a student/guest, otherwise use their role path
                                to={(userProfile.role === 'student' || userProfile.role === 'guest')
                                    ? studentDashboardPath
                                    : `/${userProfile.role}/dashboard`}
                                className="px-4 py-2 text-sm font-semibold rounded-md bg-green-100 text-green-700 hover:bg-green-200 hidden sm:inline-block transition duration-150"
                            >
                                Go to Dashboard
                            </Link>
                        ) : (
                            <button
                                onClick={handleGoogleSignIn}
                                disabled={authLoading}
                                className="flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-md bg-white text-gray-700 border border-gray-300 shadow-sm hover:shadow-md hidden sm:inline-block transition duration-150 ease-in-out disabled:opacity-50"
                            >
                                <FcGoogle className="w-5 h-5 mr-3" />
                                {authLoading ? 'Signing in...' : 'Sign in / Register'}
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto p-4 sm:p-8 space-y-10 flex-grow w-full">
                {dataLoading && <div className="text-center p-5 text-indigo-600 font-semibold">Fetching public data...</div>}
                {error && !activeModal && <div className="text-center p-5 bg-red-100 text-red-700 border border-red-300 rounded-lg">{error}</div>}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                         <div className="bg-white border border-gray-200 p-4 shadow-xl rounded-lg">
                            <div className="flex justify-between items-center border-b pb-2 mb-3">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center"><Bell className="w-5 h-5 mr-2 text-yellow-500" /> Official Announcements</h2>
                                <button onClick={() => handleModalDataFetch('advert')} className="text-indigo-600 hover:underline font-semibold text-sm flex items-center">View All <ExternalLink className="w-3 h-3 ml-1" /></button>
                            </div>
                            <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto pr-2">
                                {updates.length > 0 ? (
                                    updates.slice(0, 5).map((update, index) => (
                                        <div key={index} className="py-2 flex justify-between items-start hover:bg-gray-50">
                                             <div className="flex items-start">
                                                <div className="text-center text-xs font-bold mr-4 flex-shrink-0 w-12">
                                                    <span className="block text-indigo-600">{update.date ? new Date(update.date).toLocaleString('en-US', { month: 'short' }) : ''}</span>
                                                    <span className="block text-xl text-gray-900">{update.date ? new Date(update.date).getDate() : ''}</span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 leading-snug">{update.text}</p>
                                                    <p className="text-xs text-gray-500 mt-1">Status: {update.status || 'New'}</p>
                                                </div>
                                            </div>
                                            <a href={update.advertisementUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 flex items-center text-sm ml-4 flex-shrink-0">
                                                <Download className="w-4 h-4 mr-1" /> PDF
                                            </a>
                                        </div>
                                    ))
                                ) : ( !dataLoading && <p className="p-4 text-gray-500">No new notices currently available.</p> )}
                            </div>
                        </div>
                        <div className="pt-4">
                            <h2 className="text-2xl font-bold text-gray-800 border-b pb-3 mb-4">Exam Schedule & Registration</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {exams.length > 0 ? (
                                    exams.map(exam => ( <PublicExamCard key={exam.id || exam.paperId} exam={exam} /> ))
                                ) : ( !dataLoading && <p className="col-span-1 md:col-span-2 text-gray-500 p-4 border rounded-lg bg-white text-center">No scheduled exams currently open for registration or upcoming.</p> )}
                            </div>
                        </div>
                        {/* === NEW: LIVE EVENTS SECTION === */}
                        <div className="pt-4">
                            <h2 className="text-2xl font-bold text-gray-800 border-b pb-3 mb-4 flex items-center"><Activity className="w-6 h-6 mr-2 text-teal-600" /> Upcoming & Live Events</h2>
                            {events.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {events.slice(0, 4).map(event => (
                                        <div key={event.id} className="p-4 border rounded-lg shadow-md bg-white border-l-4 border-teal-500">
                                            <h3 className="text-lg font-bold">{event.title}</h3>
                                            <p className="text-sm text-gray-600">Mode: {event.mode?.toUpperCase()}</p>
                                            <p className="text-sm text-gray-600">Starts: {event.startAt ? new Date(event.startAt).toLocaleDateString() : 'TBD'}</p>
                                            <button onClick={() => handleModalDataFetch('events')} className="mt-2 text-indigo-600 hover:underline text-sm font-medium flex items-center">
                                                <Ticket className="w-4 h-4 mr-1" /> Book Event Ticket
                                            </button>
                                            <p className="text-xs text-teal-500 mt-1">Powered by Joyzz.Fun</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 p-4 border rounded-lg bg-white text-center">No upcoming events scheduled right now.</p>
                            )}
                        </div>
                        {/* === END: LIVE EVENTS SECTION === */}
                    </div>
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white rounded-lg shadow-xl p-4 border border-gray-200">
                             <h3 className="text-xl font-bold text-gray-800 mb-3 border-b pb-2">Quick Links</h3>
                             <div className="grid grid-cols-2 gap-3">
                                 {/* NEW ACADEMIC GROUP */}
                                 <h4 className="col-span-2 text-md font-bold text-indigo-700 mt-1">Academic & Registration</h4>
                                 <QuickLinkButton title="Apply Online" icon="UserPlus" color="green" action={() => handleModalDataFetch('apply')} />
                                 <QuickLinkButton title="Courses/Classes" icon="Zap" color="red" action={() => handleModalDataFetch('courses')} />
                                 <QuickLinkButton title="Admit Card" icon="FileText" color="blue" action={() => handleModalDataFetch('admit')} />
                                 <QuickLinkButton title="Participate in Event" icon="Ticket" color="indigo" action={() => handleModalDataFetch('events')} />

                                 {/* NEW RESULT GROUP */}
                                 <h4 className="col-span-2 text-md font-bold text-indigo-700 border-t pt-3 mt-3">Results & Access</h4>
                                 <QuickLinkButton title="Result" icon="Trophy" color="purple" action={() => handleModalDataFetch('result')} />
                                 <QuickLinkButton title="Answer Key" icon="Key" color="orange" action={() => handleModalDataFetch('answer')} />
                             </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-xl border border-gray-200">
                             <h3 className="text-xl font-bold text-gray-800 p-4 border-b flex items-center">
                                 <Trophy className="w-5 h-5 mr-2 text-yellow-600" /> Recently Declared Results
                             </h3>
                             <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto pr-2">
                                 {recentResults.length > 0 ? (
                                     recentResults.map((result, index) => (
                                         <div key={result.paperId || index} className="p-3 flex justify-between items-center hover:bg-gray-50">
                                             <div>
                                                 <h4 className="text-sm font-semibold text-purple-800">{result.paperTitle || `Results for ${result.paperId}`}</h4>
                                                 <p className="text-xs text-gray-600">Published: {result.publishedAt ? new Date(result.publishedAt).toLocaleDateString() : 'N/A'}</p>
                                             </div>
                                             <button onClick={() => handleModalDataFetch('result')} className="text-indigo-600 hover:underline text-xs font-medium">View List</button>
                                         </div>
                                     ))
                                 ) : ( !dataLoading && <p className="p-4 text-gray-500 text-sm">No recent results found.</p> )}
                             </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="bg-gray-800 p-4 text-center text-gray-400 text-sm mt-auto">
                &copy; {new Date().getFullYear()} **Math Craft Portal**. All rights reserved.
            </footer>
        </div>
    );
}
